import React, { useEffect, useState } from 'react';
import styles from './index.module.scss';
import {
  OwnersResourcesLandingViewModel,
  VehicleLookupState,
} from './models/owners-landing-model';
import { isSubaruVin, validateVIN } from '~/common/utils/carDetailValidator';
import PopupModalNative from './components/PopupModal';

import { Button, TooltipDisclaimer } from '~/common/components/ui-elements';
import {
  createInfomediaStore,
  submitManualData,
} from './components/InfomediaStore';

const OwnersResourcesLandingPage = (props: OwnersResourcesLandingViewModel) => {
  const store = createInfomediaStore();

  const [state, setState] = useState<VehicleLookupState>({
    vinNumber: '',
    vinNumberError: false,
    vinNumberErrorString: '',
    submitDisabled: false,
    showLoader: false,
    showVinPopup: false,
    vinNotFound: false,
    vinOldModel: false,
  });

  const closeVinPopup = () => {
    setState((prevState) => ({ ...prevState, showVinPopup: false }));
  };

  const handleVINChange = (value: string) => {
    const isError = validateVIN(value);
    setState((prevState) => ({
      ...prevState,
      vinNumberError: isError !== '',
      vinNumberErrorString: isError,
      vinNumber: value,
      submitDisabled: isError !== '',
      vinNotFound: false,
      vinOldModel: false,
    }));
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.vinNumber) return;
    setState((prevState) => ({
      ...prevState,
      showLoader: true,
      submitDisabled: true,
      vinNotFound: false,
      vinOldModel: false,
    }));
    findCarByVin(state.vinNumber);
  };

  const findCarByVin = async (vin: string) => {
    if (!isSubaruVin(vin)) {
      console.warn('Invalid VIN');
      setState((prev) => ({
        ...prev,
        vinNotFound: true,
        submitDisabled: false,
      }));
      return;
    }

    try {
      const result = await store.findCarByVin(vin);
      const { data, status } = result;

      if (!data?.modelYear || status === 404) {
        console.warn('VIN not found or no model year returned.');
        setState((prev) => ({
          ...prev,
          vinNotFound: true,
          showLoader: false,
          submitDisabled: false,
        }));
        return;
      }

      const modelYear = parseInt(data.modelYear, 10);
      // Check for older models
      if (modelYear <= 2023) {
        setState((prev) => ({
          ...prev,
          vinOldModel: true,
          submitDisabled: false,
        }));
        return;
      }
      // Check if manual exists for the vehicle --newer models only
      const url = `/api/manuals?modelYear=${modelYear}&vehicle=${data.model.vehicleLine}&code=${data.model.modelCode}`;
      console.log('Checking manuals at URL:', url);
      const response = await fetch(url);

      if (!response.ok) {
        console.error('Manuals check failed:', response.status);
        setState((prev) => ({
          ...prev,
          vinOldModel: true,
          showLoader: false,
          submitDisabled: false,
        }));
        return;
      }

      const resultJson = await response.json();
      const manualExists =
        typeof resultJson === 'boolean' ? resultJson : resultJson.exists;

      if (manualExists) {
        submitError(status, data);
      } else {
        setState((prev) => ({
          ...prev,
          vinOldModel: true,
          showLoader: false,
          submitDisabled: false,
        }));
      }
    } catch (error) {
      console.error('Error during VIN submission:', error);
      setState((prev) => ({
        ...prev,
        showLoader: false,
        vinNotFound: true,
        submitDisabled: false,
      }));
    }
  };

  const submitError = (status: number, data: any) => {
    if (status === 200) {
      let vehicleLine;
      if (data.model.vehicleLine === null) {
        const description = data.model.description;
        vehicleLine = description ? description.split(' ')[0] : '';
      } else {
        vehicleLine = data.model.vehicleLine;
      }
      submitManualData(data, vehicleLine);
    } else {
      setState((prevState) => ({
        ...prevState,
        vinNotFound: true,
        showLoader: false,
        submitDisabled: false,
      }));
    }
  };

  const {
    vinNumber,
    vinNumberError,
    vinNumberErrorString,
    submitDisabled,
    showLoader,
    showVinPopup,
    vinNotFound,
    vinOldModel,
  } = state;

  return (
    <div id="owners-resources" className={styles.containerRow}>
      <div className={styles.contentHeader}>
        <div
          className={styles.title}
          dangerouslySetInnerHTML={{ __html: props.headerTitle as string }}
        />
        {props.tooltipDisclaimer && (
          <TooltipDisclaimer disclaimer={props.tooltipDisclaimer} />
        )}
      </div>
      <div
        className={styles.description}
        dangerouslySetInnerHTML={{ __html: props.description as string }}
      />
      <div className={styles.vinDetail}>
        <span
          dangerouslySetInnerHTML={{ __html: props.subheading as string }}
        />
        {props.subheadingTooltip && (
          <TooltipDisclaimer disclaimer={props.subheadingTooltip} />
        )}
      </div>
      <div className={styles.row}>
        <div className={styles.col}>
          <div className={styles.vinWrapper}>
            Search by VIN{' '}
            <a
              className={styles.btnText}
              onClick={() =>
                setState((prevState) => ({
                  ...prevState,
                  showVinPopup: true,
                }))
              }
            >
              Where&apos;s my VIN?
            </a>
          </div>
          <div className={styles.fieldSpacing}>
            <input
              className={`${styles.vinInputClass} ${
                vinNumberError ? styles.errorInput : ''
              }`}
              value={state.vinNumber}
              onChange={(e) => handleVINChange(e.target.value)}
            />
          </div>
          {vinNumberError && (
            <p className={styles.errorText}>{vinNumberErrorString}</p>
          )}
          {vinNotFound && (
            <p
              className={styles.errorText}
              style={{ textAlign: 'center', padding: '15px 0' }}
            >
              ERROR! The Vehicle Identification Number (VIN) entered is not
              eligible for this service or could not be found. Please carefully
              check the VIN and try again. Alternatively, please contact the
              Subaru Customer Relations Team via&nbsp;
              <a href="mailto:feedback@subaru.com.au">feedback@subaru.com.au</a>
              . Please note – &apos;Owners Resources&apos; are only available
              for certain Model Year 2024 vehicles and newer.
            </p>
          )}
          {vinOldModel && (
            <p
              className={styles.errorText}
              style={{ textAlign: 'center', padding: '15px 0' }}
            >
              The VIN entered is not valid or could not be found. Please try
              again, or contact Subaru Customer Relations Team via&nbsp;
              <a href="mailto:feedback@subaru.com.au">feedback@subaru.com.au</a>
              . Please note – &apos;Owners Resources&apos; are only available
              for certain Model Year 2024 vehicles and newer.
            </p>
          )}
          <div className={styles.submitBtnWrapper}>
            <Button
              type="primary"
              loading={showLoader}
              disabled={submitDisabled || !vinNumber}
              onClick={onSubmit}
            >
              View
            </Button>
          </div>
        </div>
      </div>
      {showVinPopup && (
        <PopupModalNative
          content={props.popupModalContent}
          isVisible={showVinPopup}
          close={closeVinPopup}
        />
      )}
    </div>
  );
};

export default OwnersResourcesLandingPage;
